import React, { useEffect, useState } from 'react';
import { FORM_ERROR } from 'final-form';
import { Auth } from '~/amplify';
import sendPhoneVerificationCode from '~/amplify/utils/sendPhoneVerificationCode';
import { PHONE_NUMBER_ATTRIBUTE_KEY } from '~/components/page/Auth/constants';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Loading from '~/components/atom/Loading';
import TextButton from '~/components/atom/TextButton';
import { Body, Heading3, Label } from '~/components/atom/Typography';
import OTPInput from '~/components/molecule/OTPInput';
import PhoneVerificationField from '~/components/molecule/PhoneVerificationField';
import { Form, Field } from '~/components/organism/Forms';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import { reporter } from '~/hooks/useErrorReporter';
import PhoneValidation from '~/util/Validation/Phone';
import styled from 'styled-components';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import InfoBlock from '~/components/molecule/InfoBlock';
import Icon from '~/components/atom/Icon';
import confirmPhoneNumberAttribute from '~/amplify/utils/confirmPhoneNumberAttribute';
import { useProfileCognitoPhone } from '../../../../context/ProfileCognitoPhoneContext';
import Validation from '~/util/Validation';
import Catalog from '~/Catalog';
import Divider from '~/components/atom/Divider';

const text = {
  title: 'Backup telefoonnummer verifiëren',
  explanation:
    'Je backup telefoonnummer staat in je account maar is nog niet geverifieerd. Verifiëer het met een SMS-code om het te activeren als backup.',
  sendCodeIntro:
    'Stuur een verificatiecode naar je telefoonnummer en voer de code daarna hieronder in.',
  confirmButton: 'Bevestigen',
  codeInstruction: 'Voer de verificatiecode in die je per SMS hebt ontvangen.',
  invalidPhone: Catalog.mfaInvalidDutchPhoneNumber,
  invalidCode: 'Ongeldige code. Probeer het opnieuw.',
  successBody:
    'Backup telefoonnummer succesvol geverifieerd en SMS-verificatie ingeschakeld.',
  cancelButton: 'Annuleren',
  samePhoneNumberAlreadyVerified:
    'Je backup telefoonnummer is al geverifieerd.',
};

type Props = {
  onClose: () => void;
  onSmsEnabled: () => void;
};

const MFAVerifyPhoneModal: React.FC<Props> = ({ onClose, onSmsEnabled }) => {
  const {
    phoneNumber,
    phoneVerified,
    loading: cognitoPhoneLoading,
    refresh: refreshCognitoPhone,
  } = useProfileCognitoPhone();
  const [initialPhone, setInitialPhone] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const addToast = useAddToast();
  const close = () => onClose();

  useEffect(() => {
    if (cognitoPhoneLoading) {
      return;
    }

    if (!phoneNumber || phoneVerified) {
      onClose();
      return;
    }

    setInitialPhone(phoneNumber);
  }, [cognitoPhoneLoading, phoneNumber, phoneVerified, onClose]);

  const loading =
    cognitoPhoneLoading ||
    !phoneNumber ||
    phoneVerified ||
    initialPhone.length === 0;

  const onSendCode = async (phone: string) => {
    const normalizedPhone = PhoneValidation.normalizeDutchPhoneNumber(phone);

    if (!normalizedPhone) {
      setPhoneError(text.invalidPhone);
      return false;
    }

    setSendingCode(true);
    setPhoneError(null);

    try {
      if (normalizedPhone === phoneNumber && phoneVerified) {
        addToast([
          formatToastMessage(text.samePhoneNumberAlreadyVerified, 'info'),
        ]);
        onClose();
        return true;
      }

      const result = await Auth.updateUserAttribute({
        userAttribute: {
          attributeKey: PHONE_NUMBER_ATTRIBUTE_KEY,
          value: normalizedPhone,
        },
      });

      await sendPhoneVerificationCode(result);
      setSmsSent(true);
      return true;
    } catch (err) {
      reporter.captureMessage(
        `MFAVerifyPhoneModal: Failed to send phone verification code: ${err}`,
        'error',
      );
      setPhoneError(text.invalidPhone);
      return false;
    } finally {
      setSendingCode(false);
    }
  };

  const onSubmit = async ({
    phone,
    code,
  }: {
    phone: string;
    code: string;
  }): Promise<{ [key: string]: string } | void> => {
    if (!smsSent) {
      await onSendCode(phone);
      return;
    }

    try {
      await confirmPhoneNumberAttribute(code);
      await Auth.updateMFAPreference({ sms: 'ENABLED' });
      await refreshCognitoPhone();
      onSmsEnabled();
      addToast([formatToastMessage(text.successBody, 'success')]);
      onClose();
    } catch (err) {
      reporter.captureMessage(
        `MFAVerifyPhoneModal: Failed to confirm phone attribute: ${err}`,
        'error',
      );
      return { [FORM_ERROR]: text.invalidCode };
    }
  };

  const showContent = !loading && initialPhone.length > 0;

  return (
    <Overlay onClose={close}>
      <Modal onClose={close} maxWidth="520px">
        <JustificationContainer
          direction="column"
          gap="l"
          padding={['xl']}
          width="100%"
        >
          <JustificationContainer gap="s" width="100%" align="center">
            <Icon name="phone" strokeWidth={2.3} />
            <Heading3 size="m" margin={[null]}>
              {text.title}
            </Heading3>
          </JustificationContainer>

          {loading && (
            <JustificationContainer
              align="center"
              justification="center"
              padding={['l', null]}
              width="100%"
            >
              <Loading />
            </JustificationContainer>
          )}

          {showContent && (
            <>
              <InfoBlock
                appearance="info"
                padding={['base']}
                icon={{
                  name: 'info',
                  backgroundColor: { group: 'info' },
                  color: { group: 'white' },
                }}
                headerText={null}
                inline
              >
                <Body size="base" margin={[null]}>
                  {text.explanation}
                </Body>
              </InfoBlock>

              <Form
                key={initialPhone}
                initialValues={{ phone: initialPhone, code: '' }}
                onSubmit={onSubmit}
              >
                {({
                  handleSubmit,
                  submitting,
                  submitError: formError,
                  form,
                }) => (
                  <StyledForm onSubmit={handleSubmit}>
                    <JustificationContainer
                      direction="column"
                      gap="l"
                      width="100%"
                    >
                      <Body size="base" margin={[null]}>
                        {text.sendCodeIntro}
                      </Body>

                      <JustificationContainer
                        width="100%"
                        justification="space-between"
                      >
                        <JustificationContainer
                          align="center"
                          gap="xxxs"
                          direction="column"
                        >
                          <Label
                            size="base"
                            margin={[null, null, 'xxxs', null]}
                          >
                            {Catalog.phoneLabel}
                          </Label>
                          <Body size="base" margin={[null]}>
                            {Validation.Phone.maskPhone(initialPhone)}
                          </Body>
                        </JustificationContainer>
                        <PhoneVerificationField
                          onSendCode={onSendCode}
                          phoneError={phoneError}
                          submitting={submitting}
                          sendingCode={sendingCode}
                          smsSent={smsSent}
                          hidePhoneInput
                        />
                      </JustificationContainer>

                      {smsSent && (
                        <JustificationContainer
                          direction="column"
                          gap="m"
                          width="100%"
                        >
                          <Divider margin={['xxs', null, 's']} />
                          <Body size="base" margin={[null]}>
                            {text.codeInstruction}
                          </Body>
                          {formError && (
                            <Body
                              margin={[null]}
                              color={{ group: 'danger' }}
                              size="base"
                            >
                              {formError}
                            </Body>
                          )}
                          <Field name="code">
                            {({ input }) => (
                              <OTPInput
                                length={6}
                                value={input.value ?? ''}
                                onChange={input.onChange}
                                onBlur={input.onBlur}
                                disabled={submitting}
                                autoFocus
                                onComplete={() => {
                                  void form.submit();
                                }}
                              />
                            )}
                          </Field>
                        </JustificationContainer>
                      )}
                    </JustificationContainer>

                    <JustificationContainer
                      justification="end"
                      gap="base"
                      align="center"
                      margin={['l', null, null, null]}
                    >
                      <TextButton
                        label={text.cancelButton}
                        onClick={close}
                        appearance="secondary"
                        size="medium"
                      />
                      {smsSent && (
                        <Button
                          appearance="primary"
                          size="medium"
                          type="submit"
                          disabled={submitting}
                          loading={submitting}
                          label={text.confirmButton}
                        />
                      )}
                    </JustificationContainer>
                  </StyledForm>
                )}
              </Form>
            </>
          )}
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

const StyledForm = styled.form`
  width: 100%;
`;

export default MFAVerifyPhoneModal;
